import React, { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, GripVertical, Upload, Download, ShieldAlert, ShieldOff } from "lucide-react";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  Card, Field, Input, NumberInput, Textarea, Select,
  PrimaryButton, QuietButton,
} from "../../components/UI";

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const DOW = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

function Loading() {
  return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-ink-3" size={20} /></div>;
}

/* ============ Section 1 — Working calendar ============ */
export function WorkingCalendarTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/working-calendar").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return <Loading />;

  const set = (patch) => setC((s) => ({ ...s, ...patch }));
  const toggleDay = (n) => set({ working_days_of_week: c.working_days_of_week.includes(n)
    ? c.working_days_of_week.filter((d) => d !== n) : [...c.working_days_of_week, n].sort() });
  const toggleHoliday = (i) => set({ bank_holidays: c.bank_holidays.map((h, j) => j === i ? { ...h, disabled: !h.disabled } : h) });
  const addShutdown = () => {
    const y = new Date().getFullYear();
    set({ shutdown_periods: [...c.shutdown_periods, { id: uid(), label: "Christmas shutdown", start_date: `${y}-12-22`, end_date: `${y + 1}-01-02` }] });
  };
  const updShutdown = (id, patch) => set({ shutdown_periods: c.shutdown_periods.map((s) => s.id === id ? { ...s, ...patch } : s) });
  const rmShutdown = (id) => set({ shutdown_periods: c.shutdown_periods.filter((s) => s.id !== id) });

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/working-calendar", c); toast.success("Working calendar saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="working-calendar-tab">
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <Field label="Standard working day (hours)" hint="Used to convert labour hours into working days.">
            <NumberInput value={c.working_day_hours} onChange={(v) => set({ working_day_hours: Number(v) || 0 })} data-testid="wc-hours" />
          </Field>
          <Field label="Working days of the week">
            <div className="flex flex-wrap gap-1.5">
              {DOW.map(([lbl, n]) => (
                <button key={n} type="button" onClick={() => toggleDay(n)} data-testid={`wc-dow-${n}`}
                  className={["h-8 px-3 rounded-md border text-sm transition-colors",
                    c.working_days_of_week.includes(n) ? "bg-accent text-white border-accent" : "bg-surface text-ink-2 border-line hover:bg-surface-2"].join(" ")}>
                  {lbl}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>UK bank holidays (England & Wales)</h3>
          <span className="text-ink-3" style={{ fontSize: 12 }}>Disable any your firm still works.</span>
        </div>
        <div className="max-h-72 overflow-y-auto border border-line rounded-md divide-y divide-line-soft">
          {c.bank_holidays.map((h, i) => (
            <label key={h.date + i} data-testid={`wc-bh-${h.date}`}
              className="flex items-center gap-3 px-3 py-2 hover:bg-surface-2/50 cursor-pointer">
              <input type="checkbox" checked={!h.disabled} onChange={() => toggleHoliday(i)} className="accent-[#2F4F3E]" />
              <span className="font-mono tnum text-ink-3" style={{ fontSize: 12, width: 92 }}>{h.date}</span>
              <span className={["text-ink-2", h.disabled ? "line-through opacity-50" : ""].join(" ")} style={{ fontSize: 12.5 }}>{h.name}</span>
            </label>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>Company shutdown periods</h3>
          <QuietButton icon={Plus} onClick={addShutdown} data-testid="wc-add-shutdown">Add period</QuietButton>
        </div>
        {c.shutdown_periods.length === 0 ? (
          <p className="text-ink-3" style={{ fontSize: 12.5 }}>No shutdowns. Add your Christmas closure or summer break — spans are skipped in all date maths.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {c.shutdown_periods.map((s) => (
              <div key={s.id} className="flex items-center gap-2" data-testid={`wc-shutdown-${s.id}`}>
                <Input value={s.label} onChange={(v) => updShutdown(s.id, { label: v })} placeholder="Label" />
                <input type="date" value={s.start_date} onChange={(e) => updShutdown(s.id, { start_date: e.target.value })}
                  className="h-8 px-2 rounded-md border border-line bg-surface text-ink" style={{ fontSize: 12.5 }} />
                <span className="text-ink-3">→</span>
                <input type="date" value={s.end_date} onChange={(e) => updShutdown(s.id, { end_date: e.target.value })}
                  className="h-8 px-2 rounded-md border border-line bg-surface text-ink" style={{ fontSize: 12.5 }} />
                <button onClick={() => rmShutdown(s.id)} className="h-8 w-8 rounded text-ink-3 hover:text-note-red hover:bg-surface-2" data-testid={`wc-rm-shutdown-${s.id}`}><Trash2 size={13} className="mx-auto" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex justify-end">
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Save} data-testid="wc-save">Save working calendar</PrimaryButton>
      </div>
    </div>
  );
}

/* ============ Section 2 — Programme defaults ============ */
export function ProgrammeDefaultsTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  useEffect(() => { api.get("/settings/programme-defaults").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return <Loading />;

  const set = (patch) => setC((s) => ({ ...s, ...patch }));
  const updRow = (id, patch) => set({ phase_sequence: c.phase_sequence.map((r) => r.id === id ? { ...r, ...patch } : r) });
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const rows = [...c.phase_sequence];
    const from = rows.findIndex((r) => r.id === dragId);
    const to = rows.findIndex((r) => r.id === targetId);
    const [moved] = rows.splice(from, 1);
    rows.splice(to, 0, moved);
    set({ phase_sequence: rows });
    setDragId(null);
  };
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/programme-defaults", c); toast.success("Programme defaults saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const phaseOpts = [{ value: "", label: "— none —" }, ...c.phase_sequence.map((r) => ({ value: r.id, label: r.phase_name }))];

  return (
    <div className="flex flex-col gap-5" data-testid="programme-defaults-tab">
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <Field label="Default crew size on site" hint="Decimal allowed (e.g. 2.5). Drives programme durations.">
            <NumberInput value={c.default_crew_size} onChange={(v) => set({ default_crew_size: Number(v) || 0 })} data-testid="pd-crew" />
          </Field>
          <Field label="Default contingency buffer (%)" hint="Added on top of each calculated phase duration.">
            <NumberInput value={c.default_contingency_pct} onChange={(v) => set({ default_contingency_pct: Number(v) || 0 })} data-testid="pd-contingency" />
          </Field>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>Phase sequencing</h3>
        <p className="text-ink-3 mb-3" style={{ fontSize: 12 }}>Drag to reorder. Set which phase must finish first and any gap (working days) before it starts.</p>
        <div className="flex flex-col gap-1.5">
          {c.phase_sequence.map((r) => (
            <div key={r.id} draggable onDragStart={() => setDragId(r.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(r.id)}
              data-testid={`pd-row-${r.id}`}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-line bg-surface hover:bg-surface-2/50">
              <GripVertical size={13} className="text-ink-4 cursor-grab" />
              <span className="text-ink" style={{ fontSize: 12.5, fontWeight: 500, flex: 1 }}>{r.phase_name}</span>
              <span className="text-ink-3" style={{ fontSize: 11 }}>after</span>
              <div style={{ width: 190 }}>
                <Select value={r.depends_on || ""} options={phaseOpts.filter((o) => o.value !== r.id)}
                  onChange={(v) => updRow(r.id, { depends_on: v || null })} />
              </div>
              <span className="text-ink-3" style={{ fontSize: 11 }}>gap</span>
              <div style={{ width: 70 }}>
                <NumberInput value={r.gap_working_days} onChange={(v) => updRow(r.id, { gap_working_days: Math.max(0, parseInt(v || 0, 10)) })} />
              </div>
              <span className="text-ink-4" style={{ fontSize: 11 }}>days</span>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Save} data-testid="pd-save">Save programme defaults</PrimaryButton>
      </div>
    </div>
  );
}

/* ============ Section 3 — Export defaults ============ */
const SECTIONS = [["cover", "Cover page"], ["assumptions", "Assumptions"], ["exclusions", "Exclusions"],
  ["payment_terms", "Payment terms"], ["programme", "Programme"], ["drawings", "Drawings"]];

export function ExportDefaultsTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/export-defaults").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return <Loading />;

  const set = (patch) => setC((s) => ({ ...s, ...patch }));
  const toggleSection = (k) => set({ sections: { ...c.sections, [k]: !c.sections[k] } });
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/export-defaults", c); toast.success("Export defaults saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="export-defaults-tab">
      <Card className="p-6">
        <Field label="Default tender PDF format">
          <div className="flex gap-2">
            {[["itemised", "Itemised (cost per line)"], ["grouped_by_phase", "Grouped by phase (phase totals only)"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set({ default_pdf_format: v })} data-testid={`ed-fmt-${v}`}
                className={["h-9 px-4 rounded-md border text-sm transition-colors",
                  c.default_pdf_format === v ? "bg-accent text-white border-accent" : "bg-surface text-ink-2 border-line hover:bg-surface-2"].join(" ")}>{l}</button>
            ))}
          </div>
        </Field>
        <div className="mt-5">
          <span className="col-head">Sections shown by default</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {SECTIONS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => toggleSection(k)} data-testid={`ed-section-${k}`}
                className={["h-8 px-3 rounded-full border text-sm transition-colors",
                  c.sections[k] ? "bg-accent/10 text-accent border-accent/40" : "bg-surface text-ink-3 border-line hover:bg-surface-2"].join(" ")}>{l}</button>
            ))}
          </div>
        </div>
      </Card>
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <Field label="Default assumptions" hint="Pre-populates new tenders; editable per tender.">
            <Textarea value={c.default_assumptions} onChange={(v) => set({ default_assumptions: v })} rows={6} />
          </Field>
          <Field label="Default exclusions" hint="Pre-populates new tenders; editable per tender.">
            <Textarea value={c.default_exclusions} onChange={(v) => set({ default_exclusions: v })} rows={6} />
          </Field>
        </div>
      </Card>
      <div className="flex justify-end">
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Save} data-testid="ed-save">Save export defaults</PrimaryButton>
      </div>
    </div>
  );
}

/* ============ Section 4 — Provisional-sum defaults ============ */
export function ProvsumDefaultsTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/provsum-defaults").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return <Loading />;

  const visits = c.visits;
  const total = Math.round(visits.reduce((s, v) => s + (Number(v.pct) || 0), 0) * 100) / 100;
  const ok = Math.abs(total - 100) < 0.01 && visits.length >= 2 && visits.length <= 3 && visits.every((v) => v.name.trim());
  const upd = (i, patch) => setC((s) => ({ ...s, visits: s.visits.map((v, j) => j === i ? { ...v, ...patch } : v) }));
  const setTwo = () => setC({ visits: [{ name: "First fix", pct: 60 }, { name: "Second fix", pct: 40 }] });
  const setThree = () => setC({ visits: [{ name: "First fix", pct: 40 }, { name: "Second fix", pct: 40 }, { name: "Final fix", pct: 20 }] });
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/provsum-defaults", { visits }); toast.success("Provisional-sum defaults saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="provsum-defaults-tab">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>Subcontractor visit split</h3>
          <div className="flex gap-2">
            <QuietButton onClick={setTwo} data-testid="ps-two-way">Two-way</QuietButton>
            <QuietButton onClick={setThree} data-testid="ps-three-way">Three-way</QuietButton>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {visits.map((v, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`ps-visit-${i}`}>
              <div style={{ flex: 1 }}><Input value={v.name} onChange={(val) => upd(i, { name: val })} placeholder="Visit name" /></div>
              <div style={{ width: 110 }}><NumberInput value={v.pct} onChange={(val) => upd(i, { pct: Number(val) || 0 })} /></div>
              <span className="text-ink-3" style={{ fontSize: 12.5 }}>%</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2" data-testid="ps-total">
          <span className="col-head">Total</span>
          <span className="font-mono tnum" style={{ fontSize: 13, fontWeight: 700, color: ok ? "#2F4F3E" : "#A63A2E" }}>{total}%</span>
          {!ok && <span style={{ fontSize: 12, color: "#A63A2E" }}>must total 100% across 2–3 named visits</span>}
        </div>
      </Card>
      <div className="flex justify-end">
        <PrimaryButton onClick={save} disabled={saving || !ok} icon={saving ? Loader2 : Save} data-testid="ps-save">Save provisional-sum defaults</PrimaryButton>
      </div>
    </div>
  );
}

/* ============ Section 5 — Insurance ============
 *
 * Cover figures + current certificate PDF + expiry state. The status
 * (`ok` / `expiring_soon` / `expired` / `missing`) is server-derived
 * so this tab and the dashboard card cannot disagree.
 *
 * Colour tokens: `warning` for expiring_soon, `note-red` for expired /
 * missing. No new colours introduced. */
function fmtDateUK(iso) {
  if (!iso) return "\u2014";
  try {
    const d = new Date(iso); const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  } catch { return "\u2014"; }
}
// Display helper for the expiry input — server stores ISO, we display UK.
// Idempotent: if the user has typed DD/MM/YYYY into the field, we keep it
// as-is so their keystrokes aren't clobbered mid-edit; if the string looks
// like ISO we transform it for display.
function isoToUKInput(v) {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  return s;
}
function fmtBytes(n) {
  if (!n && n !== 0) return "\u2014";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function StatusPill({ status, days, hasCert }) {
  // `missing` splits into two — no cert vs cert-without-expiry — so this
  // pill and the dashboard card carry the same distinction.
  const missingLabel = hasCert ? "No expiry date set" : "No certificate on file";
  const map = {
    ok:            { bg: "#DDEBE0", fg: "#2F4F3E", icon: ShieldCheck, label: "Cover in date" },
    expiring_soon: { bg: "#FEF3E2", fg: "#B45309", icon: ShieldAlert, label: `Expires in ${days} day${days === 1 ? "" : "s"}` },
    expired:       { bg: "#FEE2E2", fg: "#B91C1C", icon: ShieldOff,   label: `Expired ${Math.abs(days || 0)} day${Math.abs(days || 0) === 1 ? "" : "s"} ago` },
    missing:       { bg: "#FEE2E2", fg: "#B91C1C", icon: ShieldOff,   label: missingLabel },
  };
  const cfg = map[status] || map.missing;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md"
      style={{ background: cfg.bg, color: cfg.fg, fontSize: 11.5, fontWeight: 600 }}
      data-testid="insurance-status-pill"
    >
      <Icon size={13} /> {cfg.label}
    </span>
  );
}

/* ============ Section 6 — Document Copy (LT-13b) ============
 *
 * Editable copy for client-facing PDFs. Held in the `document-copy`
 * settings doc so wording can change without a code change + a deploy.
 *
 * BLANK-VS-DEFAULT SEMANTIC (matters):
 *   Backend `PUT /settings/document-copy` stores blank text fields VERBATIM
 *   (i.e. keeps them empty). At render time `load_document_copy` falls
 *   back to the current live default when a field is empty. So the
 *   per-field "Reset" link CLEARS the input rather than pasting the
 *   default text into it — that way, if the shipped default ever
 *   improves, tenants who have "Reset"-ed a field silently pick up the
 *   new wording. Tenants who typed the old wording explicitly keep it.
 *
 *   Never change Reset to paste-the-default-in; that would freeze the
 *   current copy onto every tenant who used it, defeating the point.
 */
export function DocumentCopyTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = React.useCallback(async () => {
    const r = await api.get("/settings/document-copy"); setC(r.data);
  }, []);
  useEffect(() => { reload().catch(() => setC({})); }, [reload]);
  if (!c) return <Loading />;

  const set = (patch) => setC((s) => ({ ...s, ...patch }));
  const clear = (key) => set({ [key]: "" });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/document-copy", {
        ao_intro:       c.ao_intro || "",
        letter_thanks:  c.letter_thanks || "",
        letter_closing: c.letter_closing || "",
        signatory_name: c.signatory_name || null,
        signatory_role: c.signatory_role || null,
      });
      // Reload so we render whatever the server actually persisted —
      // blanks stay blank, so the placeholder text takes over and the
      // user can see the "live default" state.
      await reload();
      toast.success("Document copy saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const ResetLink = ({ fieldKey }) => (
    <button
      type="button"
      onClick={() => clear(fieldKey)}
      className="text-accent hover:underline"
      style={{ fontSize: 11 }}
      data-testid={`document-copy-reset-${fieldKey.replace(/_/g, "-")}`}
    >
      Reset
    </button>
  );

  const LabelRow = ({ label, fieldKey }) => (
    <div className="flex items-center justify-between">
      <span className="col-head" style={{ fontSize: 10 }}>{label}</span>
      <ResetLink fieldKey={fieldKey} />
    </div>
  );

  return (
    <div className="flex flex-col gap-5" data-testid="document-copy-tab">
      <Card className="p-6">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
          Cover letter
        </h3>
        <p className="text-ink-3 mb-4" style={{ fontSize: 12 }}>
          Wording on the client cover letter (page 1 of the estimate pack). Leave a field blank to use the live default.
        </p>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <LabelRow label="Opening thanks" fieldKey="letter_thanks" />
            <div data-testid="document-copy-letter-thanks">
              <Textarea
                value={c.letter_thanks || ""}
                onChange={(v) => set({ letter_thanks: v })}
                rows={4}
                placeholder={c.defaults?.letter_thanks || ""}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <LabelRow label="Closing paragraph" fieldKey="letter_closing" />
            <div data-testid="document-copy-letter-closing">
              <Textarea
                value={c.letter_closing || ""}
                onChange={(v) => set({ letter_closing: v })}
                rows={4}
                placeholder={c.defaults?.letter_closing || ""}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1.5">
              <LabelRow label="Signatory name" fieldKey="signatory_name" />
              <div data-testid="document-copy-signatory-name">
                <Input
                  value={c.signatory_name || ""}
                  onChange={(v) => set({ signatory_name: v })}
                  placeholder={c.defaults?.signatory_name || ""}
                />
              </div>
              <span className="text-ink-4" style={{ fontSize: 11 }}>Prints under the sign-off.</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <LabelRow label="Signatory role" fieldKey="signatory_role" />
              <div data-testid="document-copy-signatory-role">
                <Input
                  value={c.signatory_role || ""}
                  onChange={(v) => set({ signatory_role: v })}
                  placeholder="e.g. Director"
                />
              </div>
              <span className="text-ink-4" style={{ fontSize: 11 }}>Optional. Shown on the line below the name.</span>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-display text-ink mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
          Assumptions &amp; Omissions
        </h3>
        <p className="text-ink-3 mb-4" style={{ fontSize: 12 }}>
          Wording on the Assumptions &amp; Omissions sheet. Leave blank to use the live default.
        </p>
        <div className="flex flex-col gap-1.5">
          <LabelRow label="Introduction" fieldKey="ao_intro" />
          <div data-testid="document-copy-ao-intro">
            <Textarea
              value={c.ao_intro || ""}
              onChange={(v) => set({ ao_intro: v })}
              rows={4}
              placeholder={c.defaults?.ao_intro || ""}
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Save} data-testid="document-copy-save">
          Save document copy
        </PrimaryButton>
      </div>
    </div>
  );
}

export function InsuranceTab() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const reload = React.useCallback(async () => {
    const r = await api.get("/settings/insurance"); setC(r.data);
  }, []);
  useEffect(() => { reload().catch(() => setC({})); }, [reload]);
  if (!c) return <Loading />;

  const set = (patch) => setC((s) => ({ ...s, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/insurance", {
        employers_liability_amount: Number(c.employers_liability_amount) || 0,
        public_liability_amount:    Number(c.public_liability_amount) || 0,
        insurer_name:               c.insurer_name || null,
        policy_number:              c.policy_number || null,
        expiry_date:                c.expiry_date || null,
      });
      await reload();
      toast.success("Insurance settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Certificate must be a PDF");
      return;
    }
    setUploading(true);
    const form = new FormData(); form.append("file", file);
    try {
      await api.post("/settings/insurance/certificate", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await reload();
      toast.success("Certificate uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const view = () => {
    const url = api.defaults.baseURL + "/settings/insurance/certificate";
    window.open(url, "_blank", "noopener");
  };

  const remove = async () => {
    if (!window.confirm("Remove the current insurance certificate?")) return;
    try {
      await api.delete("/settings/insurance/certificate");
      await reload();
      toast.success("Certificate removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  const hasCert = Boolean(c.certificate_file_id);

  return (
    <div className="flex flex-col gap-5" data-testid="insurance-tab">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            Cover levels
          </h3>
          <StatusPill status={c.status} days={c.days_until_expiry} hasCert={Boolean(c.certificate_file_id)} />
        </div>
        <div className="grid grid-cols-2 gap-6">
          <Field label="Employers' Liability (£)">
            <NumberInput
              value={c.employers_liability_amount ?? 10000000}
              onChange={(v) => set({ employers_liability_amount: Number(v) || 0 })}
              data-testid="insurance-employers-liability"
            />
          </Field>
          <Field label="Public Liability (£)">
            <NumberInput
              value={c.public_liability_amount ?? 5000000}
              onChange={(v) => set({ public_liability_amount: Number(v) || 0 })}
              data-testid="insurance-public-liability"
            />
          </Field>
          <Field label="Insurer">
            <Input
              value={c.insurer_name || ""}
              onChange={(v) => set({ insurer_name: v })}
              placeholder="e.g. Hiscox"
              data-testid="insurance-insurer-name"
            />
          </Field>
          <Field label="Policy number">
            <Input
              value={c.policy_number || ""}
              onChange={(v) => set({ policy_number: v })}
              placeholder="e.g. 12345678"
              data-testid="insurance-policy-number"
            />
          </Field>
          <Field label="Expiry date (DD/MM/YYYY)">
            <Input
              value={isoToUKInput(c.expiry_date)}
              onChange={(v) => set({ expiry_date: v })}
              placeholder="30/06/2027"
              data-testid="insurance-expiry-date"
            />
            {hasCert && c.status === "missing" ? (
              <div
                className="mt-1 text-note-red"
                style={{ fontSize: 11.5 }}
                data-testid="insurance-expiry-missing-hint"
              >
                Add an expiry date so renewal can be tracked.
              </div>
            ) : null}
          </Field>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-display text-ink mb-3" style={{ fontSize: 14, fontWeight: 600 }}>
          Certificate PDF
        </h3>
        {hasCert ? (
          <div className="flex items-center justify-between gap-4 p-3 border border-line rounded-md bg-surface-2/40">
            <div className="flex flex-col gap-0.5">
              <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }} data-testid="insurance-cert-filename">
                {c.certificate_filename || "certificate.pdf"}
              </span>
              <span className="text-ink-3" style={{ fontSize: 11.5 }}>
                {fmtBytes(c.certificate_size_bytes)} · uploaded {fmtDateUK(c.certificate_uploaded_at)}
                {c.certificate_uploaded_by_name ? ` by ${c.certificate_uploaded_by_name}` : ""}
                {c.expiry_date ? ` · expires ${fmtDateUK(c.expiry_date)}` : ""}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <QuietButton onClick={view} icon={Download} data-testid="insurance-cert-view">View</QuietButton>
              <label className="cursor-pointer">
                <input type="file" accept="application/pdf,.pdf" onChange={(e) => upload(e.target.files?.[0])}
                       className="hidden" data-testid="insurance-cert-replace-input" />
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line hover:bg-surface-2 text-ink-2"
                      style={{ fontSize: 12 }}>
                  <Upload size={12} /> Replace
                </span>
              </label>
              <QuietButton onClick={remove} icon={Trash2} data-testid="insurance-cert-remove">Remove</QuietButton>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 p-4 border border-dashed border-line rounded-md">
            <span className="text-ink-3" style={{ fontSize: 12.5 }}>
              No certificate on file. Upload the current PDF — max 10 MB.
            </span>
            <label className="cursor-pointer">
              <input type="file" accept="application/pdf,.pdf" onChange={(e) => upload(e.target.files?.[0])}
                     className="hidden" data-testid="insurance-cert-upload-input" />
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white hover:opacity-90"
                    style={{ fontSize: 12, fontWeight: 500 }}>
                <Upload size={12} /> {uploading ? "Uploading…" : "Upload PDF"}
              </span>
            </label>
          </div>
        )}
      </Card>

      <div className="flex justify-end">
        <PrimaryButton onClick={save} disabled={saving} icon={saving ? Loader2 : Save} data-testid="insurance-save">
          Save insurance settings
        </PrimaryButton>
      </div>
    </div>
  );
}

